import React, { useState, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
// Chakra imports
import { Portal, Box, useDisclosure } from '@chakra-ui/react';
// Layout components
import Navbar from 'components/navbar/NavbarRTL.js';
import Sidebar from 'components/sidebar/Sidebar.js';
// import Footer from 'components/footer/FooterAdmin.js';
import { RtlProvider } from 'components/rtlProvider/RtlProvider.js';
import { SidebarContext } from 'contexts/SidebarContext';
import routes from 'routes.js';
import OnboardingOverlay from 'components/overlay/OnboardingOverlay.jsx';

// Custom Chakra theme
export default function Dashboard(props) {
  document.documentElement.dir = 'rtl';
  const { ...rest } = props;
  // states and functions
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [showOnboardingOverlay, setShowOnboardingOverlay] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  const { onOpen } = useDisclosure();

  // Check if company onboarding is complete
  useEffect(() => {
    const checkOnboardingStatus = () => {
      try {
        const userDataStr = localStorage.getItem('user');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          if (userData && userData.info && userData.info.companyOnboardingStatus !== undefined) {
            const status = userData.info.companyOnboardingStatus;
            setOnboardingStatus(status);
            // Show overlay if status is PENDING_SETUP (100) or SETUP_IN_PROGRESS (200)
            setShowOnboardingOverlay(status === 100 || status === 200);
          } else {
            setShowOnboardingOverlay(false);
          }
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setShowOnboardingOverlay(false);
      }
    };

    checkOnboardingStatus();
  }, []);

  const accessibleRoutes = routes.filter(
    (route) => route.layout === '/rtl' && (!route.accessible || route.accessible()),
  );

  // Failsafe - if no routes are accessible, force access to rtl-default
  if (accessibleRoutes.length === 0) {
    const defaultRoute = routes.find((r) => r.path === '/rtl-default');
    if (defaultRoute) {
      defaultRoute.accessible = () => true;
    }
  }

  // functions for changing the states from components
  const getRoute = () => {
    return window.location.pathname !== '/rtl/full-screen-maps';
  };

  const getActiveRoute = (routes) => {
    let activeRoute = 'Default Brand Text';
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else if (routes[i].category) {
        let categoryActiveRoute = getActiveRoute(routes[i].items);
        if (categoryActiveRoute !== activeRoute) {
          return categoryActiveRoute;
        }
      } else {
        if (window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1) {
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbar(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };

  const getActiveNavbarText = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbarText(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbarText(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1) {
          return routes[i].messageNavbar;
        }
      }
    }
    return activeNavbar;
  };

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === '/rtl') {
        const isAccessible = prop.accessible ? prop.accessible() : true;

        if (!isAccessible) {
          return null;
        }

        return <Route path={prop.layout + prop.path} component={prop.component} key={key} />;
      }
      if (prop.collapse) {
        return getRoutes(prop.items);
      }
      if (prop.category) {
        return getRoutes(prop.items);
      } else {
        return null;
      }
    });
  };

  return (
    <RtlProvider>
      <SidebarContext.Provider
        value={{
          toggleSidebar,
          setToggleSidebar,
        }}
      >
        <Sidebar routes={routes} display="none" {...rest} />
        <Box
          float="left"
          minHeight="100vh"
          height="100%"
          overflow="auto"
          position="relative"
          maxHeight="100%"
          w={{ base: '100%', xl: 'calc( 100% - 290px )' }}
          maxWidth={{ base: '100%', xl: 'calc( 100% - 290px )' }}
          transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
          transitionDuration=".2s, .2s, .35s"
          transitionProperty="top, bottom, width"
          transitionTimingFunction="linear, linear, ease"
        >
          <Portal>
            <Box>
              <Navbar
                onOpen={onOpen}
                logoText={'Horizon UI Dashboard'}
                brandText={getActiveRoute(routes)}
                secondary={getActiveNavbar(routes)}
                message={getActiveNavbarText(routes)}
                fixed={fixed}
                {...rest}
              />
            </Box>
          </Portal>

          {getRoute() ? (
            <Box mx="auto" p={{ base: '20px', md: '30px' }} pe="20px" minH="100vh" pt="50px">
              <Switch>
                {getRoutes(routes)}
                <Redirect from="/" to={accessibleRoutes.length > 0 ? `/rtl${accessibleRoutes[0].path}` : '/rtl/rtl-default'} />
              </Switch>
            </Box>
          ) : null}

          {/* Onboarding Overlay */}
          <OnboardingOverlay
            isVisible={showOnboardingOverlay}
            onboardingStatus={onboardingStatus}
          />
        </Box>
      </SidebarContext.Provider>
    </RtlProvider>
  );
}
